import { useWorkout } from '../../hooks/useWorkout';
import {
  dayjs,
  formatDistance,
  formatFriendlyDuration,
  formatWorkoutPace,
} from '../../lib/utils';
import RouteMap from './RouteMap';
import { useState } from 'react';
import { Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { useAccessToken } from '../../hooks/useProfile';
import { useQueryClient } from '@tanstack/react-query';

interface Exercise {
  movement: string;
  sets: Set[];
}

interface Set {
  reps: number;
  weight: number;
}

interface WorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workoutData: any; // This will be the actual workout data from the hook
  activityId: string;
  createdAt?: number;
}

export default function WorkoutModal({
  isOpen,
  onClose,
  workoutData,
  activityId,
  createdAt,
}: WorkoutModalProps) {
  // The workoutData is already passed from WorkoutCard (which uses the hook)
  // But we could also use the hook here for consistency
  const { data: hookWorkoutData, isLoading, error } = useWorkout(activityId);
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  // Use hook data as it's more complete, fallback to passed data
  const fullWorkoutData = hookWorkoutData || workoutData;

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedExercises, setEditedExercises] = useState<Exercise[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize editing data when entering edit mode
  const handleStartEdit = () => {
    setEditedExercises(fullWorkoutData?.exercises || []);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedExercises([]);
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/workouts/${fullWorkoutData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken.data}`,
          },
          body: JSON.stringify({
            exercises: editedExercises,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update workout');
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ['workout', activityId],
      });
      await queryClient.invalidateQueries({ queryKey: ['workouts'] });

      setIsEditing(false);
      setEditedExercises([]);
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message || 'Failed to update workout', type: 'error' } }));
    } finally {
      setIsSaving(false);
    }
  };

  // Exercise editing functions
  const addExercise = () => {
    const newExercise: Exercise = {
      movement: '',
      sets: [{ reps: 0, weight: 0 }],
    };
    setEditedExercises([...editedExercises, newExercise]);
  };

  const removeExercise = (exerciseIndex: number) => {
    setEditedExercises(
      editedExercises.filter((_, index) => index !== exerciseIndex),
    );
  };

  const updateExercise = (exerciseIndex: number, updatedExercise: Exercise) => {
    setEditedExercises(
      editedExercises.map((exercise, index) =>
        index === exerciseIndex ? updatedExercise : exercise,
      ),
    );
  };

  const addSet = (exerciseIndex: number) => {
    const exercise = editedExercises[exerciseIndex];
    if (exercise) {
      const newSet: Set = { reps: 0, weight: 0 };
      const updatedExercise = {
        ...exercise,
        sets: [...exercise.sets, newSet],
      };
      updateExercise(exerciseIndex, updatedExercise);
    }
  };

  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const exercise = editedExercises[exerciseIndex];
    if (exercise && exercise.sets.length > 1) {
      const updatedExercise = {
        ...exercise,
        sets: exercise.sets.filter((_, index) => index !== setIndex),
      };
      updateExercise(exerciseIndex, updatedExercise);
    }
  };

  const updateSet = (
    exerciseIndex: number,
    setIndex: number,
    updatedSet: Set,
  ) => {
    const exercise = editedExercises[exerciseIndex];
    if (exercise) {
      const updatedExercise = {
        ...exercise,
        sets: exercise.sets.map((set, index) =>
          index === setIndex ? updatedSet : set,
        ),
      };
      updateExercise(exerciseIndex, updatedExercise);
    }
  };

  if (!isOpen) return null;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Use hook data if available
  const displayData = fullWorkoutData;
  const completedDate = fullWorkoutData?.completedAt;
  const plannedDate = createdAt;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="surface-card w-full max-w-md max-h-[80vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Workout Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {isLoading && !displayData ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading workout details...</div>
          </div>
        ) : null}

        {error && !displayData ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">{error?.message}</div>
            <div className="text-sm text-gray-500">
              Unable to load workout details
            </div>
          </div>
        ) : null}

        {displayData ? (
          <div className="space-y-4">
            {/* Workout Title */}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {displayData.title}
            </h3>
            {completedDate ? (
              <p className="text-sm text-gray-500">
                Completed on {formatDate(completedDate)}
              </p>
            ) : null}
            {!completedDate && plannedDate ? (
              <p className="text-sm text-gray-500">
                Planned for {dayjs(plannedDate).format('dddd, MMMM D')}
              </p>
            ) : null}

            {/* Route Map */}
            {fullWorkoutData ? (
              fullWorkoutData.polylineEncoded ||
              (fullWorkoutData.startLatitude &&
                fullWorkoutData.startLongitude) ? (
                <div className="py-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Route Map
                  </h4>
                  <RouteMap
                    polylineEncoded={fullWorkoutData.polylineEncoded}
                    startLatitude={fullWorkoutData.startLatitude}
                    startLongitude={fullWorkoutData.startLongitude}
                    endLatitude={fullWorkoutData.endLatitude}
                    endLongitude={fullWorkoutData.endLongitude}
                    className="h-48 w-full"
                  />
                </div>
              ) : null
            ) : null}

            {/* Workout Stats */}
            <div className="grid grid-cols-2 gap-4 py-4 border-t border-gray-200">
              {displayData.type ? (
                <div className="text-center">
                  <div className="text-2xl mb-1">📝</div>
                  <div className="text-sm font-medium text-gray-900 capitalize">
                    {displayData.type}
                  </div>
                  <div className="text-xs text-gray-500">Type</div>
                </div>
              ) : null}

              {displayData.distance && displayData.distance > 0 ? (
                <div className="text-center">
                  <div className="text-2xl mb-1">📏</div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDistance(displayData.distance)}
                  </div>
                  <div className="text-xs text-gray-500">Distance</div>
                </div>
              ) : null}

              {displayData.duration || displayData.movingTime ? (
                <div className="text-center">
                  <div className="text-2xl mb-1">⏱️</div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatFriendlyDuration(
                      displayData.duration || displayData.movingTime || 0,
                    )}
                  </div>
                  <div className="text-xs text-gray-500">Duration</div>
                </div>
              ) : null}

              {displayData.distance && displayData.movingTime ? (
                <div className="text-center">
                  <div className="text-2xl mb-1">🏃</div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatWorkoutPace(
                      displayData.type,
                      displayData.distance,
                      displayData.movingTime,
                    )}
                  </div>
                  <div className="text-xs text-gray-500">Pace</div>
                </div>
              ) : null}
            </div>

            {/* Additional Stats (only if full data is loaded) */}
            {fullWorkoutData.averageHeartRate &&
            fullWorkoutData.maxHeartRate ? (
              <div className="grid grid-cols-2 gap-4 py-4 border-t border-gray-200">
                {fullWorkoutData.averageHeartRate &&
                fullWorkoutData.averageHeartRate > 0 ? (
                  <div className="text-center">
                    <div className="text-2xl mb-1">❤️</div>
                    <div className="text-sm font-medium text-gray-900">
                      {Math.round(fullWorkoutData.averageHeartRate)} bpm
                    </div>
                    <div className="text-xs text-gray-500">Avg Heart Rate</div>
                  </div>
                ) : null}

                {fullWorkoutData.maxHeartRate &&
                fullWorkoutData.maxHeartRate > 0 ? (
                  <div className="text-center">
                    <div className="text-2xl mb-1">💓</div>
                    <div className="text-sm font-medium text-gray-900">
                      {Math.round(fullWorkoutData.maxHeartRate)} bpm
                    </div>
                    <div className="text-xs text-gray-500">Max Heart Rate</div>
                  </div>
                ) : null}

                {fullWorkoutData.elevationGain &&
                fullWorkoutData.elevationGain > 0 ? (
                  <div className="text-center">
                    <div className="text-2xl mb-1">⛰️</div>
                    <div className="text-sm font-medium text-gray-900">
                      {Math.round(fullWorkoutData.elevationGain * 3.28084)} ft
                    </div>
                    <div className="text-xs text-gray-500">Elevation Gain</div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Exercises (for strength workouts) */}
            {displayData.type === 'strength' ? (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Exercises
                  </h4>
                  {!isEditing ? (
                    (displayData.exercises &&
                      displayData.exercises.length > 0) ||
                    !displayData.exercises ? (
                      <button
                        onClick={handleStartEdit}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-brand-cobalt hover:text-brand-cobalt"
                      >
                        <Edit2 className="h-3 w-3" />
                        Edit
                      </button>
                    ) : null
                  ) : null}
                  {isEditing ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-brand-green hover:text-brand-midnight disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </div>

                {/* Display mode */}
                {!isEditing ? (
                  <div className="space-y-3">
                    {displayData.exercises &&
                    displayData.exercises.length > 0 ? (
                      displayData.exercises.map(
                        (exercise: any, exerciseIndex: number) => (
                          <div
                            key={exerciseIndex}
                            className="bg-gray-50 rounded-lg p-3"
                          >
                            <h5 className="font-medium text-gray-900 mb-2">
                              {exercise.movement}
                            </h5>
                            <div className="space-y-1">
                              {exercise.sets.map(
                                (set: any, setIndex: number) => (
                                  <div
                                    key={setIndex}
                                    className="flex items-center justify-between text-sm text-gray-700"
                                  >
                                    <span>Set {setIndex + 1}:</span>
                                    <span>
                                      {set.reps} reps @ {set.weight} lbs
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        ),
                      )
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No exercises added yet. Click Edit to add exercises.
                      </p>
                    )}
                  </div>
                ) : null}

                {/* Edit mode */}
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <button
                        onClick={addExercise}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-brand-cobalt hover:text-brand-cobalt"
                      >
                        <Plus className="h-4 w-4" />
                        Add Exercise
                      </button>
                    </div>

                    {editedExercises.map((exercise, exerciseIndex) => (
                      <div
                        key={exerciseIndex}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <input
                            type="text"
                            value={exercise.movement}
                            onChange={(e) =>
                              updateExercise(exerciseIndex, {
                                ...exercise,
                                movement: e.target.value,
                              })
                            }
                            className="flex-1 rounded-md border border-gray-300 px-3 py-1 text-sm focus:ring-2 focus:ring-brand-volt focus:border-brand-volt"
                            placeholder="Exercise name (e.g., Bench Press)"
                          />
                          <button
                            onClick={() => removeExercise(exerciseIndex)}
                            className="ml-2 p-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-600">
                              Sets
                            </span>
                            <button
                              onClick={() => addSet(exerciseIndex)}
                              className="text-sm text-brand-cobalt hover:text-brand-cobalt"
                            >
                              + Add Set
                            </button>
                          </div>

                          {exercise.sets.map((set, setIndex) => (
                            <div
                              key={setIndex}
                              className="flex items-center gap-2 p-2 bg-white rounded border"
                            >
                              <span className="text-sm text-gray-600 w-8">
                                {setIndex + 1}.
                              </span>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  value={set.reps || ''}
                                  onChange={(e) =>
                                    updateSet(exerciseIndex, setIndex, {
                                      ...set,
                                      reps: parseInt(e.target.value) || 0,
                                    })
                                  }
                                  className="w-16 rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-brand-volt focus:border-brand-volt"
                                  placeholder="0"
                                />
                                <span className="text-sm text-gray-600">
                                  reps
                                </span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.25"
                                  value={set.weight || ''}
                                  onChange={(e) =>
                                    updateSet(exerciseIndex, setIndex, {
                                      ...set,
                                      weight: parseFloat(e.target.value) || 0,
                                    })
                                  }
                                  className="w-20 rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-brand-volt focus:border-brand-volt"
                                  placeholder="0"
                                />
                                <span className="text-sm text-gray-600">
                                  lbs
                                </span>
                              </div>
                              {exercise.sets.length > 1 ? (
                                <button
                                  onClick={() =>
                                    removeSet(exerciseIndex, setIndex)
                                  }
                                  className="ml-auto p-1 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {editedExercises.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No exercises added yet. Click "Add Exercise" to get
                        started.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Description */}
            {fullWorkoutData?.description ? (
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Notes
                </h4>
                <p className="text-sm text-gray-700">
                  {fullWorkoutData.description}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex justify-between border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-400">
            {activityId && `Activity ID: ${activityId}`}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-brand-volt text-brand-midnight rounded-lg hover:bg-brand-volt/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
